
import { useEffect, useState, useCallback } from "react"
import { getAllReportsByVeterinary, getAllVeterinariesStats } from "../../services/ReportService"
import { useAuth } from "../../contexts/authContext"
import {
  ClipboardList,
  Search,
  Calendar,
  Building,
  FileText,
  User,
  Activity,
  ChevronLeft,
  ChevronRight,
} from "lucide-react"
import "../../assets/styles/Users.css"
import "../../assets/styles/Pagination.css"
import { formatDate } from "../../utils/dates/formatDate"

const Reports = () => {
  const { getToken } = useAuth()
  const [reportsByVeterinary, setReportsByVeterinary] = useState({})
  const [loading, setLoading] = useState(true)
  const [statsLoading, setStatsLoading] = useState(true)
  const [searchTerm, setSearchTerm] = useState("")
  const [selectedVeterinary, setSelectedVeterinary] = useState("all")
  const [totalReports, setTotalReports] = useState(0)
  const [currentPage, setCurrentPage] = useState(1)
  const [totalPages, setTotalPages] = useState(1)
  const [pageSize] = useState(50)
  const [allVeterinaries, setAllVeterinaries] = useState([])
  const [stats, setStats] = useState({
    totalVeterinaries: 0,
    totalReports: 0,
    completedReports: 0,
  })

  const fetchReports = useCallback(
    async (page) => {
      setLoading(true)
      try {
        const token = await getToken()
        const data = await getAllReportsByVeterinary(token, page, pageSize)
        setReportsByVeterinary(data.reports_by_veterinary || {})
        setTotalReports(data.total || 0)
        setTotalPages(data.total_pages || 1)
      } catch (error) {
        console.error("Error al obtener los reportes:", error)
      } finally {
        setLoading(false)
      }
    },
    [getToken, pageSize],
  )

  const fetchStats = useCallback(async () => {
    setStatsLoading(true)
    try {
      const token = await getToken()
      const data = await getAllVeterinariesStats(token)
      setStats({
        totalVeterinaries: data.total_veterinaries || 0,
        totalReports: data.total_reports || 0,
        completedReports: data.completed_reports || 0,
      })
      setAllVeterinaries(data.all_veterinaries || [])
    } catch (error) {
      console.error("Error al obtener estadísticas:", error)
    } finally {
      setStatsLoading(false)
    }
  }, [getToken])

  useEffect(() => {
    fetchReports(currentPage)
    fetchStats()
  }, [currentPage, fetchReports, fetchStats])

  useEffect(() => {
    setCurrentPage(1)
  }, [selectedVeterinary])

  const handlePageChange = (newPage) => {
    if (newPage >= 1 && newPage <= totalPages) {
      setCurrentPage(newPage)
    }
  }

  const getAllReports = () => {
    const allReports = []
    Object.entries(reportsByVeterinary).forEach(([veterinary, reports]) => {
      reports.forEach((report) => {
        allReports.push({
          ...report,
          veterinaryName: veterinary,
        })
      })
    })
    return allReports
  }

  const getFilteredReports = () => {
    const allReports = getAllReports()

    return allReports.filter((report) => {
      const matchesSearch =
        (report.patient?.name || "").toLowerCase().includes(searchTerm.toLowerCase()) ||
        (report.patient?.guardian?.name || "").toLowerCase().includes(searchTerm.toLowerCase()) ||
        (report.requestingProfessional?.name || "").toLowerCase().includes(searchTerm.toLowerCase())

      const matchesVeterinary = selectedVeterinary === "all" || report.veterinaryName === selectedVeterinary

      return matchesSearch && matchesVeterinary
    })
  }

  const filteredReports = getFilteredReports()

  return (
    <div className="users-container">
      <div className="users-header">
        <h1 className="users-title">
          <ClipboardList className="users-icon" />
          Reportes por Veterinaria
        </h1>
        <div className="search-container">
          <Search className="search-icon" />
          <input
            type="text"
            placeholder="Buscar reportes..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="search-input"
          />
        </div>
      </div>

      <div className="filter-container">
        <div className="filter-wrapper">
          <label htmlFor="veterinary-filter" className="filter-label">
            Filtrar por veterinaria:
          </label>
          <select
            id="veterinary-filter"
            value={selectedVeterinary}
            onChange={(e) => setSelectedVeterinary(e.target.value)}
            className="veterinary-filter"
          >
            <option value="all">Todas las veterinarias</option>
            {statsLoading ? (
              <option disabled>Cargando veterinarias...</option>
            ) : (
              allVeterinaries.map((vet) => (
                <option key={vet} value={vet}>
                  {vet || "Sin nombre"}
                </option>
              ))
            )}
          </select>
          <Building className="filter-icon" />
        </div>
      </div>

      <div className="users-stats">
        <div className="stat-card">
          <Building className="stat-icon active" />
          <div className="stat-info">
            <span className="stat-value">{statsLoading ? "..." : stats.totalVeterinaries}</span>
            <span className="stat-label">Veterinarias</span>
          </div>
        </div>
        <div className="stat-card">
          <FileText className="stat-icon total" />
          <div className="stat-info">
            <span className="stat-value">{statsLoading ? "..." : stats.totalReports}</span>
            <span className="stat-label">Total Reportes</span>
          </div>
        </div>
        <div className="stat-card">
          <Activity className="stat-icon inactive" />
          <div className="stat-info">
            <span className="stat-value">{statsLoading ? "..." : stats.completedReports}</span>
            <span className="stat-label">Reportes Completados</span>
          </div>
        </div>
      </div>

      <div className="users-table-container">
        {loading ? (
          <table className="users-table">
            <thead>
              <tr>
                <th>Veterinaria</th>
                <th>Tutor</th>
                <th>Profesional</th>
                <th>Tipo</th>
                <th>Fecha</th>
                <th>Estado</th>
              </tr>
            </thead>
            <tbody>
              {[...Array(5)].map((_, index) => (
                <tr key={index} className="user-row skeleton-row">
                  <td>
                    <div className="skeleton skeleton-text-lg"></div>
                  </td>
                  <td>
                    <div className="skeleton skeleton-text-lg"></div>
                  </td>
                  <td>
                    <div className="skeleton skeleton-text-lg"></div>
                  </td>
                  <td>
                    <div className="skeleton skeleton-text-sm"></div>
                  </td>
                  <td>
                    <div className="skeleton skeleton-text-sm"></div>
                  </td>
                  <td>
                    <div className="skeleton skeleton-text-sm"></div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        ) : (
          <>
            <table className="users-table">
              <thead>
                <tr>
                  <th>Veterinaria</th>
                  <th>Tutor</th>
                  <th>Profesional</th>
                  <th>Tipo</th>
                  <th>Fecha</th>
                  <th>Estado</th>
                </tr>
              </thead>
              <tbody>
                {filteredReports.length === 0 ? (
                  <tr>
                    <td colSpan="6">
                      <div className="no-results">
                        <p>No se encontraron reportes que coincidan con la búsqueda.</p>
                      </div>
                    </td>
                  </tr>
                ) : (
                  filteredReports.map((report) => (
                    <tr key={report.id} className="user-row">
                      <td>
                        <div className="report-veterinary">
                          <Building className="info-icon" />
                          {report.veterinaryName || "Sin nombre"}
                        </div>
                      </td>
                      <td>
                        <div className="report-guardian">
                          <User className="info-icon" />
                          {report.patient?.guardian?.name || "Sin tutor"}
                        </div>
                      </td>
                      <td>
                        <div className="report-professional">
                          {report.requestingProfessional?.name || "Sin profesional"}
                        </div>
                      </td>
                      <td>
                        <div className="report-type">
                          <FileText className="info-icon" />
                          {report.type}
                        </div>
                      </td>
                      <td>
                        <div className="report-date">
                          <Calendar className="info-icon" />
                          {formatDate(report.creationDate)}
                        </div>
                      </td>
                      <td>
                        <div
                          className={`report-status status-${report.status === "Completado" ? "completed" : "progress"}`}
                        >
                          {report.status}
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>

            {/* Pagination controls */}
            <div className="pagination-controls">
              <button
                className="pagination-button"
                onClick={() => handlePageChange(currentPage - 1)}
                disabled={currentPage === 1}
              >
                <ChevronLeft className="pagination-icon" />
                Anterior
              </button>

              <div className="pagination-info">
                Página {currentPage} de {totalPages} • {totalReports} reportes en total
              </div>

              <button
                className="pagination-button"
                onClick={() => handlePageChange(currentPage + 1)}
                disabled={currentPage === totalPages}
              >
                Siguiente
                <ChevronRight className="pagination-icon" />
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  )
}

export default Reports
